import { LitElement, css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { tokens } from '../../theme/tokens.css';

export interface SegmentOption {
  value: string;
  label: string;
}

/**
 * Segmented Control - A group of mutually exclusive options
 *
 * @element segmented-control
 * @fires change - Fired when selection changes, detail contains { value: string }
 */
@customElement('segmented-control')
export class SegmentedControl extends LitElement {
  static override styles = [
    tokens,
    css`
      :host {
        display: block;
      }

      .container {
        display: inline-flex;
        background-color: var(--color-bg-selector);
        border-radius: var(--radius-md);
        padding: 3px;
        gap: 2px;
      }

      .segment {
        padding: 8px 16px;
        border: none;
        background: transparent;
        color: var(--color-text-secondary);
        /* A <button> does not inherit font-family — the UA stylesheet gives it
           its own. Without this the labels render in Arial while every other
           word in the settings row is MontserratWeb. */
        font-family: var(--font-family);
        font-size: var(--font-size-sm);
        font-weight: 500;
        cursor: pointer;
        border-radius: var(--radius-sm);
        /* Tapped in the settings rows on mobile — kill the double-tap-zoom
           hesitation and the grey tap flash. */
        touch-action: manipulation;
        -webkit-tap-highlight-color: transparent;
        transition:
          background-color 0.15s ease,
          color 0.15s ease;
      }

      /* Hover-only — gated so a tap on touch doesn't flash the hover style
         (this control lives in the settings rows, tapped on mobile). */
      @media (hover: hover) {
        .segment:hover:not(.selected) {
          background-color: var(--color-overlay-subtle);
          color: var(--color-text-white);
        }
      }

      .segment.selected {
        background-color: var(--color-btn-play);
        color: var(--color-text-white);
        box-shadow: var(--shadow-control);
      }
    `,
  ];

  /** Available options (JS property only, not an HTML attribute) */
  @property({ attribute: false })
  options: SegmentOption[] = [];

  /** Currently selected value */
  @property({ type: String })
  value = '';

  private handleClick(e: Event, optionValue: string) {
    e.stopPropagation();
    if (optionValue === this.value) return;
    this.value = optionValue;
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: optionValue },
        bubbles: true,
        composed: true,
      }),
    );
  }

  override render() {
    return html`
      <div class="container">
        ${this.options.map(
          (option) => html`
            <button
              type="button"
              class="segment ${option.value === this.value ? 'selected' : ''}"
              @click=${(e: Event) => this.handleClick(e, option.value)}
            >
              ${option.label}
            </button>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'segmented-control': SegmentedControl;
  }
}
